import AWS from 'aws-sdk'
import { useRef } from 'react'
import { Table, Entity } from 'dynamodb-toolbox'
import { format, startOfWeek } from 'date-fns'

let DocumentClient
export const getDynamoToolbox = () => {
  DocumentClient =
    DocumentClient ||
    (AWS.config.credentials
      ? new AWS.DynamoDB.DocumentClient({ dynamoDbCrc32: false })
      : null)

  if (!DocumentClient) return null

  const AppTable = new Table({
    name: 'data',
    partitionKey: 'pk',
    sortKey: 'sk',
    indexes: {
      gsi1: { partitionKey: 'gsi1pk', sortKey: 'gsi1sk' },
      gsi2: { partitionKey: 'gsi2pk', sortKey: 'gsi2sk' },
    },
    attributes: {
      pk: 'string',
      sk: 'string',
    },
    DocumentClient,
  })

  const User = new Entity({
    table: AppTable,
    name: 'USER',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      email: { type: 'string' },
      first_name: { type: 'string' },
      middle_initial: { type: 'string' },
      last_name: { type: 'string' },
      type: { type: 'string' },
      course_ids: { type: 'list' },
      student_group_ids: { type: 'list' },
    },
  })

  const Course = new Entity({
    table: AppTable,
    name: 'COURSE',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      assignment_ids: { type: 'list' },
      instructor_ids: { type: 'list' },
      assessments: { type: 'list' },
      gsi1pk: {
        type: 'string',
        default: (data) => `COURSE#${data.sk.split('#')[1]}`,
      },
      gsi1sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const Survey = new Entity({
    table: AppTable,
    name: 'SURVEY',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      fields: { type: 'list' },
      gsi1pk: { type: 'string', alias: 'course_id', prefix: 'COURSE#' },
      gsi1sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const StudentGroup = new Entity({
    table: AppTable,
    name: 'STUDENTGROUP',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      max_cohort_size: { type: 'number' },
      student_ids: { type: 'list' },
      assignment_groupings: { type: 'list' },
      gsi1pk: { type: 'string', alias: 'course_id', prefix: 'COURSE#' },
      gsi1sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const Student = new Entity({
    table: AppTable,
    name: 'STUDENT',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      assignment_id: { type: 'string' },
      course_id: { type: 'string' },
      first_name: { type: 'string' },
      last_name: { type: 'string' },
      middle_initial: { type: 'string' },
      attributes: { type: 'map' },
      gsi1pk: {
        type: 'string',
        alias: 'student_group_id',
        prefix: 'STUDENTGROUP#',
      },
      gsi1sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const Assessment = new Entity({
    table: AppTable,
    name: 'ASSESSMENT',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      type: { type: 'string' },
      is_official: { type: 'boolean' },
      student_ratio: { type: 'number' },
      team_size: { type: 'number' },
      duration: { type: 'number' },
      events: { type: 'list' },
      gsi1pk: { type: 'string', prefix: 'COURSE#', alias: 'course_id' },
      gsi1sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const Attempt = new Entity({
    table: AppTable,
    name: 'ATTEMPT',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      scores: { type: 'list' },
      notes: { type: 'string' },
      assessment_id: { type: 'string' },
      event_id: { type: 'string' },
      attempted_at: { type: 'string' },
      attempt_index: { type: 'number' },
      gsi1pk: { type: 'string', alias: 'student_id', prefix: 'STUDENT#' },
      gsi1sk: { type: 'string', default: (data) => data.sk },
      gsi2pk: {
        type: 'string',
        alias: 'assessment_instance_id',
        prefix: 'INSTANCE#',
      },
      gsi2sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const Disqualification = new Entity({
    table: AppTable,
    name: 'DISQUALIFICATION',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      event_id: { type: 'string' },
      assessment_id: { type: 'string' },
      reason: { type: 'string' },
      notes: { type: 'string' },
      gsi1pk: { type: 'string', alias: 'student_id', prefix: 'STUDENT#' },
      gsi1sk: { type: 'string', default: (data) => data.sk },
      gsi2pk: {
        type: 'string',
        alias: 'assessment_instance_id',
        prefix: 'INSTANCE#',
      },
      gsi2sk: { type: 'string', default: (data) => data.sk },
    },
  })

  const Instance = new Entity({
    table: AppTable,
    name: 'INSTANCE',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      assessment_id: { type: 'string' },
      student_group_id: { type: 'string' },
      cohortLabel: { type: 'string' },
      date: { type: 'string' },
      is_official: { type: 'boolean' },
      started_at: { type: 'string' },
      venue: { type: 'string' },
      address: { type: 'string' },
      started_by: { type: 'string' },
      lanes: { type: 'list' },
      gsi1pk: {
        type: 'string',
        onUpdate: true,
        default: (data) =>
          `INSTANCES#${format(startOfWeek(new Date(data.date)), 'MM-dd-yyyy')}`,
      },
      gsi1sk: {
        type: 'string',
        default: (data) => data.sk,
      },
    },
  })

  const Event = new Entity({
    table: AppTable,
    name: 'EVENT',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
      max_attempts: { type: 'number' },
      fallback_event_id: { type: 'string' },
      behaviors: { type: 'list' },
      grade_entire_team: { type: 'boolean' },
      score_aggregation_type: { type: 'string' },
      scoring_type: { type: 'string' },
    },
  })

  const Assignment = new Entity({
    table: AppTable,
    name: 'ASSIGNMENT',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
    },
  })

  const CourseScheduleInstance = new Entity({
    table: AppTable,
    name: 'COURSESCHEDULEINSTANCE',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      course_id: { type: 'string' },
      start_date: { type: 'string' },
      venue: { type: 'string' },
      instances: { type: 'list' },
      student_group_id: { type: 'string' },
    },
  })

  const Ranking = new Entity({
    table: AppTable,
    name: 'RANKING',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
    },
  })

  const MAJCOM = new Entity({
    table: AppTable,
    name: 'MAJCOM',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
    },
  })

  const AFSC = new Entity({
    table: AppTable,
    name: 'AFSC',
    timestamps: true,
    createdAlias: 'created_at',
    attributes: {
      pk: { partitionKey: true },
      sk: { sortKey: true },
      id: ['sk', 1, { save: false }],
      archived_at: { type: 'string' },
      name: { type: 'string' },
    },
  })

  let base = {
    Table: AppTable,
    User,
    Course,
    Survey,
    StudentGroup,
    Student,
    Assessment,
    Attempt,
    Disqualification,
    Instance,
    Assignment,
    Event,
    CourseScheduleInstance,
    Ranking,
    MAJCOM,
    AFSC,
  }

  Object.entries(base).forEach(([k, v]) => {
    base[k.toUpperCase()] = v
  })

  return base
}

export const useDynamoToolbox = () => {
  const toolbox = useRef(getDynamoToolbox())
  return toolbox.current
}
